import * as React from 'react';
import {CSSProperties, PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {Theme} from '../../types/Theme';

export interface ThemeRootProps {
  theme?: Theme;
  className?: string;
  style?: React.CSSProperties;
}

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeRoot = (props: PropsWithChildren<ThemeRootProps>) => {
  const {
    theme: themeProp = 'system',
    className,
    style,
    children
  } = props;

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(resolveSystemTheme);

  // Listen to OS color-scheme changes
  useEffect(
    () => {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    },
    []
  );

  const appliedTheme: 'light' | 'dark' = useMemo(
    () => {
      if (themeProp === 'system') {
        return systemTheme;
      }
      return themeProp;
    },
    [themeProp, systemTheme]
  );

  return React.createElement(
    'div',
    {
      'data-theme': appliedTheme,
      className,
      style: {
        height: '100vh',
        width: '100vw',
        ...style
      } as CSSProperties,
      children
    }
  );
};
